import { ExpandMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    lighten,
    styled,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import React, { useMemo, useState } from 'react';
import {
    useEditUserMutation,
    useGetTeamListQuery,
    useGetUserListQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import CustomAutocomplete from '../../../layout/styledcomponents/CustomAutoComplete';
import UserRole from '../../../shared/AuthRoles';
import { capitalizeName } from '../../../shared/utils';
import { buildUsersListUrl } from '../User Management/userListQueryUrl';
import toast from '../../shared/Toast';
import type { Team } from './TeamList';

type AssignableUser = {
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    isActive?: boolean;
    department?: { id: number; name?: string } | null;
};

type EditUserTrigger = (args: [string, { department: { id: number } }]) => {
    unwrap: () => Promise<unknown>;
};

function displayName(user: AssignableUser) {
    const name = [user.firstName, user.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    return name || 'User';
}

function teamLabel(user: AssignableUser) {
    return user.department?.name
        ? capitalizeName(user.department.name)
        : 'No team';
}

type PersonOption = {
    user: AssignableUser;
    label: string;
    group: string;
};

function personOption(user: AssignableUser): PersonOption {
    const name = displayName(user);
    return {
        user,
        label: user.email ? `${name} (${user.email})` : name,
        group: teamLabel(user),
    };
}

const filterPersonOptions = createFilterOptions<PersonOption>();

const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor: lighten(theme.palette.primary.light, 0.85),
}));

const GroupItems = styled('ul')({
    padding: 0,
});

async function assignUsersToTeam(
    editUser: EditUserTrigger,
    companyId: number,
    users: AssignableUser[],
    departmentId: number,
) {
    const results = await Promise.allSettled(
        users.map((user) =>
            editUser([
                `/guests/${companyId}/users/${user.id}`,
                { department: { id: departmentId } },
            ]).unwrap(),
        ),
    );

    const failed = users.filter(
        (_, index) => results[index].status === 'rejected',
    );

    return {
        succeeded: users.length - failed.length,
        failed,
    };
}

function reportAssignment(
    action: 'added' | 'moved',
    users: AssignableUser[],
    succeeded: number,
    failed: AssignableUser[],
) {
    const failedNames = failed.map(displayName).join(', ');

    if (failed.length === 0) {
        if (users.length === 1) {
            toast.success(
                action === 'added'
                    ? `${displayName(users[0])} has been added to this team`
                    : `${displayName(users[0])} has been moved`,
            );
        } else {
            toast.success(
                action === 'added'
                    ? `${succeeded} people have been added to this team`
                    : `${succeeded} people have been moved`,
            );
        }
        return;
    }

    if (succeeded === 0) {
        toast.error(`Failed to update ${failedNames}`);
        return;
    }

    const people = succeeded === 1 ? 'person' : 'people';
    toast.error(`${succeeded} ${people} ${action}. Failed: ${failedNames}`);
}

type AddPeopleDialogProps = {
    open: boolean;
    onClose: () => void;
    teamId: number;
    teamName?: string;
};

export function AddPeopleDialog({
    open,
    onClose,
    teamId,
    teamName,
}: AddPeopleDialogProps) {
    const loggedInUser = UserRole();
    const listUrl = buildUsersListUrl(loggedInUser);
    const { data: companyUsers, isFetching } = useGetUserListQuery(listUrl, {
        skip: !open || !listUrl,
    });
    const [editUser] = useEditUserMutation();
    const [inputValue, setInputValue] = useState('');
    const [selected, setSelected] = useState<PersonOption[]>([]);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
        () => new Set(),
    );
    const [saving, setSaving] = useState(false);
    const fullScreen = useMediaQuery('(max-width:600px)');

    const options = useMemo(() => {
        const users = (companyUsers ?? []) as AssignableUser[];
        return users
            .filter(
                (user) =>
                    user.isActive !== false && user.department?.id !== teamId,
            )
            .map(personOption)
            .sort(
                (a, b) =>
                    a.group.localeCompare(b.group) ||
                    a.label.localeCompare(b.label),
            );
    }, [companyUsers, teamId]);

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!loggedInUser?.company || selected.length === 0) return;
        const chosen = selected.map((option) => option.user);
        setSaving(true);
        try {
            const { succeeded, failed } = await assignUsersToTeam(
                editUser,
                loggedInUser.company,
                chosen,
                teamId,
            );
            reportAssignment('added', chosen, succeeded, failed);
            if (succeeded > 0) onClose();
        } finally {
            setSaving(false);
        }
    };

    const selectAllInGroup = (group: string) => {
        setSelected((current) => {
            const picked = new Set(current.map((option) => option.user.id));
            const toAdd = options.filter(
                (option) =>
                    option.group === group && !picked.has(option.user.id),
            );
            return [...current, ...toAdd];
        });
        setInputValue('');
    };

    const teamDisplay = teamName ? capitalizeName(teamName) : 'this team';
    const teamAdminCount = selected.filter(
        (option) =>
            option.user.role === 'guest_department_admin' &&
            option.user.department?.id != null,
    ).length;
    const noOptionsText =
        isFetching && options.length === 0
            ? 'Loading people…'
            : options.length === 0
              ? 'No people available to add.'
              : 'No matching people.';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle>
                Add people
                <IconButton
                    onClick={handleClose}
                    size='small'
                    aria-label='close'
                    disabled={saving}
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Select people to move to {teamDisplay}.
                </DialogContentText>
                {teamAdminCount > 0 ? (
                    <Alert severity='warning' sx={{ mb: 2 }}>
                        {teamAdminCount === 1 && selected.length === 1
                            ? "This user is a Team Admin. By changing this user's Team, they will no longer be able to see and manage other users under that team."
                            : 'Team Admins in this selection will no longer be able to see and manage other users under that team.'}
                    </Alert>
                ) : null}
                <CustomAutocomplete<PersonOption, true, false, false>
                    multiple
                    disableCloseOnSelect
                    filterSelectedOptions
                    disabled={saving}
                    options={options}
                    value={selected}
                    inputValue={inputValue}
                    onInputChange={(_, value, reason) => {
                        setInputValue(reason === 'reset' ? '' : value);
                    }}
                    groupBy={(option) => option.group}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                        option.user.id === value.user.id
                    }
                    filterOptions={(available, params) => {
                        const picked = new Set(
                            selected.map((option) => option.user.id),
                        );
                        return filterPersonOptions(available, params).filter(
                            (option) => !picked.has(option.user.id),
                        );
                    }}
                    onChange={(_, next) => {
                        setSelected(next);
                        setInputValue('');
                    }}
                    noOptionsText={noOptionsText}
                    renderGroup={(params) => {
                        const group = params.group;
                        const isExpanded = !collapsedGroups.has(group);
                        const remaining = options.some(
                            (option) =>
                                option.group === group &&
                                !selected.some(
                                    (picked) =>
                                        picked.user.id === option.user.id,
                                ),
                        );
                        const toggleGroup = () => {
                            setCollapsedGroups((current) => {
                                const updated = new Set(current);
                                if (updated.has(group)) updated.delete(group);
                                else updated.add(group);
                                return updated;
                            });
                        };

                        return (
                            <li key={params.key}>
                                <GroupHeader
                                    role='group'
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 0.5,
                                    }}
                                >
                                    <Box
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={toggleGroup}
                                        sx={{
                                            display: 'flex',
                                            minWidth: 0,
                                            alignItems: 'center',
                                            gap: 0.25,
                                            flex: 1,
                                        }}
                                    >
                                        <IconButton
                                            type='button'
                                            size='small'
                                            aria-label={
                                                isExpanded
                                                    ? 'Collapse group'
                                                    : 'Expand group'
                                            }
                                            sx={{ p: 0.25 }}
                                        >
                                            <ExpandMore
                                                sx={{
                                                    fontSize: '1.1rem',
                                                    transform: isExpanded
                                                        ? 'rotate(0deg)'
                                                        : 'rotate(-90deg)',
                                                    transition: (theme) =>
                                                        theme.transitions.create(
                                                            'transform',
                                                            { duration: 150 },
                                                        ),
                                                }}
                                            />
                                        </IconButton>
                                        <Typography
                                            component='span'
                                            variant='caption'
                                            fontWeight={600}
                                            sx={{
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                            }}
                                        >
                                            {capitalizeName(group)}
                                        </Typography>
                                    </Box>
                                    <Button
                                        type='button'
                                        size='small'
                                        variant='text'
                                        disabled={!remaining || saving}
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() => selectAllInGroup(group)}
                                        sx={{
                                            minWidth: 0,
                                            flexShrink: 0,
                                            fontSize: '11px',
                                            textTransform: 'none',
                                            ml: 'auto',
                                        }}
                                    >
                                        Select all
                                    </Button>
                                </GroupHeader>
                                <Collapse
                                    in={isExpanded}
                                    timeout='auto'
                                    unmountOnExit={false}
                                    sx={{ width: '100%' }}
                                >
                                    <GroupItems>{params.children}</GroupItems>
                                </Collapse>
                            </li>
                        );
                    }}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.user.id}
                                label={option.label}
                                size='small'
                            />
                        ))
                    }
                    fontSize='12px'
                    fontWeight={500}
                />
            </DialogContent>
            <DialogActions>
                <CustomButton onClick={handleClose} disabled={saving}>
                    Cancel
                </CustomButton>
                <CustomButton
                    variant='contained'
                    color='primary'
                    onClick={handleConfirm}
                    disabled={saving || selected.length === 0}
                    loading={saving}
                >
                    Add
                </CustomButton>
            </DialogActions>
        </Dialog>
    );
}

type MoveToTeamDialogProps = {
    open: boolean;
    onClose: () => void;
    onMoved: () => void;
    teamId: number;
    selectedUsers: AssignableUser[];
};

export function MoveToTeamDialog({
    open,
    onClose,
    onMoved,
    teamId,
    selectedUsers,
}: MoveToTeamDialogProps) {
    const loggedInUser = UserRole();
    const { data: teams } = useGetTeamListQuery(loggedInUser?.company, {
        skip: !open,
    });
    const [editUser] = useEditUserMutation();
    const [destination, setDestination] = useState<Team | null>(null);
    const [saving, setSaving] = useState(false);
    const fullScreen = useMediaQuery('(max-width:600px)');

    const otherTeams = useMemo(
        () => (teams ?? []).filter((team) => team.id !== teamId),
        [teams, teamId],
    );

    const teamAdminCount = selectedUsers.filter(
        (user) => user.role === 'guest_department_admin',
    ).length;

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!loggedInUser?.company || !destination) return;
        setSaving(true);
        try {
            const { succeeded, failed } = await assignUsersToTeam(
                editUser,
                loggedInUser.company,
                selectedUsers,
                destination.id,
            );
            reportAssignment('moved', selectedUsers, succeeded, failed);
            if (succeeded > 0) {
                onMoved();
                onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    const countLabel =
        selectedUsers.length === 1
            ? displayName(selectedUsers[0])
            : `${selectedUsers.length} people`;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle>
                Move to team
                <IconButton
                    onClick={handleClose}
                    size='small'
                    aria-label='close'
                    disabled={saving}
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {otherTeams.length === 0 ? (
                    <DialogContentText>
                        Create another team before moving people.
                    </DialogContentText>
                ) : (
                    <>
                        <DialogContentText sx={{ mb: 2 }}>
                            Move {countLabel} to another team.
                        </DialogContentText>
                        {teamAdminCount > 0 ? (
                            <Alert severity='warning' sx={{ mb: 2 }}>
                                {teamAdminCount === 1 &&
                                selectedUsers.length === 1
                                    ? "This user is a Team Admin. By changing this user's Team, they will no longer be able to see and manage other users under that team."
                                    : 'Team Admins in this selection will no longer be able to see and manage other users under that team.'}
                            </Alert>
                        ) : null}
                        <Autocomplete
                            options={otherTeams}
                            value={destination}
                            onChange={(_, value) => setDestination(value)}
                            getOptionLabel={(team) => capitalizeName(team.name)}
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Team'
                                    placeholder='Select a team'
                                />
                            )}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <CustomButton onClick={handleClose} disabled={saving}>
                    Cancel
                </CustomButton>
                <CustomButton
                    variant='contained'
                    color='primary'
                    onClick={handleConfirm}
                    disabled={
                        saving || otherTeams.length === 0 || destination == null
                    }
                    loading={saving}
                >
                    Move
                </CustomButton>
            </DialogActions>
        </Dialog>
    );
}
